import {
  handleRequestErrorOrThrow,
  isUnauthorizedError,
  removeProspectIdFromStorage,
  useProspect,
  withAxiosErrorCast,
} from '@insify/funnel-components';
import axios from 'axios';
import { useInfiniteQuery, useQuery } from 'react-query';

import { storage } from '../../storage';
import {
  ACTIVITIES_KEY,
  CHECKOUT_KEY,
  COMPANY_SEARCH_KEY,
  CONDITIONS_KEY,
  HIGH_CARE_CONDITIONS_QUERY_KEY,
  PARAMS_KEY,
  PRICING_KEY,
  PROSPECT_STATUS_QUERY_KEY,
  STRIPE_INTENT_KEY,
} from '../models/queries';

import { useCommands } from './useCommands';

import type { Activity } from '../models/activity';
import type { Checkout } from '../models/checkout';
import type { RegisteredCompany } from '../models/company';
import type { HighCareConditions } from '../models/funnel';
import type { Pricing } from '../models/pricing';
import type { Conditions, Params, PaymentWidgetData, ProspectStatusResponse } from '../models/queries';
import type { AxiosResponse } from 'axios';
import type { UseInfiniteQueryResult, UseQueryResult } from 'react-query';

const BASE_URL = window.insify_runtime_config.API_ENDPOINT;

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});
client.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (isUnauthorizedError(error)) {
      window.history.replaceState({}, '', '/fr/already-applied');
    }

    if (error.response.status === 404 && error.response.data === 'Aggregate is deleted') {
      removeProspectIdFromStorage(storage);
      window.location.reload();
    }

    return Promise.reject(error);
  },
);

const get = async (...args: Parameters<typeof client.get>): Promise<AxiosResponse> =>
  withAxiosErrorCast()(() => client.get(...args));

export const useParamsQuery = (): UseQueryResult<Partial<Params>> => {
  const prospectId = useProspect();
  return useQuery<Params>(
    [PARAMS_KEY],
    async () => (await get(`frenchCompanyPolicyHolder_${prospectId}/${PARAMS_KEY}`)).data,
    { onError: handleRequestErrorOrThrow },
  );
};

export const invokeStripeIntentQuery = async (prospectId: string): Promise<PaymentWidgetData> => {
  const result = await get(`frenchCompanyPolicyHolder_${prospectId}/${STRIPE_INTENT_KEY}`);
  return result.data;
};

export const useStripeIntentQuery = (): UseQueryResult<PaymentWidgetData> => {
  const prospectId = useProspect();
  return useQuery<PaymentWidgetData>([STRIPE_INTENT_KEY], async () => invokeStripeIntentQuery(prospectId), {
    onError: handleRequestErrorOrThrow,
  });
};

export const useConditionsQuery = (): UseQueryResult<Partial<Conditions>> => {
  const prospectId = useProspect();
  return useQuery<Conditions>(
    [CONDITIONS_KEY],
    async () => (await get(`frenchCompanyPolicyHolder_${prospectId}/${CONDITIONS_KEY}`)).data,
    { onError: handleRequestErrorOrThrow },
  );
};

export const useActivitiesQuery = (): UseQueryResult<Activity[]> => {
  const prospectId = useProspect();
  return useQuery<Activity[]>(
    [ACTIVITIES_KEY],
    async () => (await get(`frenchCompanyPolicyHolder_${prospectId}/${ACTIVITIES_KEY}`)).data,
    { onError: handleRequestErrorOrThrow },
  );
};

export const invokeCheckoutQuery = async (prospectId: string): Promise<Checkout> => {
  const result = await get(`frenchCompanyPolicyHolder_${prospectId}/${CHECKOUT_KEY}`);
  return result.data;
};

export const useCheckoutQuery = (): UseQueryResult<Checkout> => {
  const prospectId = useProspect();
  return useQuery<Checkout>([CHECKOUT_KEY], async () => invokeCheckoutQuery(prospectId), {
    onError: handleRequestErrorOrThrow,
  });
};

// We use an external API to fetch the price for Wakam.
// We had a situation when this API was unavailable and
// customers were able to proceed with the funnel with 0 price.
// To prevent this we make the request to the pricing API and check if it's available.
// The pricing API will return 422 if the pricing couldn't be calculated because of missing parameters,
// and it will return some 5** error if the pricing service is down.
export const usePricingQuery = (): UseQueryResult<Pricing> => {
  const { sendCommand } = useCommands();
  return useQuery(
    [PRICING_KEY],
    () => {
      console.log('usePricingQuery callback');
      return sendCommand({ type: 'requestPricing', payload: {} });
    },
    {
      onError: handleRequestErrorOrThrow,
    },
  );
};

export const useHighCareConditions = (): UseQueryResult<HighCareConditions> => {
  const prospectId = useProspect();
  return useQuery<HighCareConditions>(
    HIGH_CARE_CONDITIONS_QUERY_KEY,
    async () => (await get(`frenchCompanyPolicyHolder_${prospectId}/${HIGH_CARE_CONDITIONS_QUERY_KEY}`)).data,
    {
      staleTime: Infinity,
      onError: handleRequestErrorOrThrow,
    },
  );
};

export const useCompanySearchQuery = (query: string): UseInfiniteQueryResult<RegisteredCompany[]> => {
  const { sendCommand } = useCommands();
  return useInfiniteQuery<RegisteredCompany[]>(
    [COMPANY_SEARCH_KEY, query],
    ({ pageParam = 1 }): RegisteredCompany[] => {
      const trimmedQuery = query.trim();
      if (/[a-zA-Z0-9]/.test(trimmedQuery)) {
        return sendCommand({
          type: 'searchCompany',
          payload: { query, page: pageParam },
        }) as unknown as RegisteredCompany[];
      }
      return [];
    },
    {
      staleTime: Infinity,
      onError: handleRequestErrorOrThrow,
    },
  );
};

export const useProspectStatusQuery = (): UseQueryResult<Partial<ProspectStatusResponse>> => {
  const prospectId = useProspect();
  return useQuery<ProspectStatusResponse>(
    [PROSPECT_STATUS_QUERY_KEY],
    async () => (await get(`frenchCompanyPolicyHolder_${prospectId}/${PROSPECT_STATUS_QUERY_KEY}`)).data,
    { onError: handleRequestErrorOrThrow },
  );
};
